import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/services/api";
import {
  Search,
  ExternalLink,
  RefreshCw,
  Building2,
  Euro,
  Target,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Radar,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Flame,
  MapPin,
  Zap,
  UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import OportunidadModal from "@/components/OportunidadModal";
import DescartarModal from "@/components/DescartarModal";
import ConvertirLeadModal from "@/components/ConvertirLeadModal";
import DateRangeFilter from "@/components/DateRangeFilter";

// Score level config
const getScoreLevel = (score) => {
  if (score >= 85) return { level: "critico", color: "bg-red-500", textColor: "text-red-400", label: "Crítico" };
  if (score >= 70) return { level: "alto", color: "bg-orange-500", textColor: "text-orange-400", label: "Alto" };
  if (score >= 50) return { level: "medio", color: "bg-yellow-500", textColor: "text-yellow-400", label: "Medio" };
  return { level: "bajo", color: "bg-slate-500", textColor: "text-slate-400", label: "Bajo" };
};

// Estado config
const estadoConfig = {
  nueva: { icon: AlertCircle, color: "text-cyan-400", bg: "bg-cyan-500/20", label: "Nuevo" },
  revisada: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/20", label: "En revisión" },
  contactada: { icon: Building2, color: "text-purple-400", bg: "bg-purple-500/20", label: "Contactada" },
  convertida: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/20", label: "Convertida" },
  descartada: { icon: XCircle, color: "text-slate-400", bg: "bg-slate-500/20", label: "Descartada" },
};

// Colores para usuarios (cada usuario tiene su color único)
const userColors = [
  { bg: "bg-cyan-600", text: "text-cyan-400", border: "border-cyan-500" },
  { bg: "bg-purple-600", text: "text-purple-400", border: "border-purple-500" },
  { bg: "bg-orange-600", text: "text-orange-400", border: "border-orange-500" },
  { bg: "bg-green-600", text: "text-green-400", border: "border-green-500" },
  { bg: "bg-pink-600", text: "text-pink-400", border: "border-pink-500" },
  { bg: "bg-yellow-600", text: "text-yellow-400", border: "border-yellow-500" },
  { bg: "bg-red-600", text: "text-red-400", border: "border-red-500" },
  { bg: "bg-indigo-600", text: "text-indigo-400", border: "border-indigo-500" },
];

// Mapeo de servicios sugeridos por tipo SRS (espejo del usado en ConvertirLeadModal)
const SERVICIOS_POR_TIPO = {
  "Soporte IT / Helpdesk": ["Soporte IT Remoto", "Field Services", "Help Desk"],
  "Cableado Estructurado": ["Cableado Cat6/Fibra", "Certificación Redes"],
  "Infraestructura / CPD": ["Instalación CPD", "Mantenimiento Infraestructura"],
  "Cloud & Virtualización": ["Migración Cloud", "VMware/Virtualización"],
  "Ciberseguridad / ENS": ["Auditoría Seguridad", "Consultoría ENS/ISO27001"],
  "Comunicaciones Unificadas": ["VoIP/Telefonía", "Videoconferencia"],
  "Healthcare IT": ["Integración HIS/RIS", "Soporte Sanitario"],
  "Fotovoltaica / Energía": ["Inspección FV Drones", "Mantenimiento Solar"],
  "Drones / Cartografía": ["Drones Inspección", "Topografía/Fotogrametría"],
  "Consultoría Técnica": ["Consultoría IT", "Diseño Arquitectura"],
  "Refuerzo Ejecución": ["Staff Augmentation", "Proyectos Llave en Mano"],
};

// Si la oportunidad tiene los datos mínimos del adjudicatario, devuelve un
// payload listo para POST /oportunidades/{id}/convertir.
// Si falta contacto o email, devuelve null y el caller debe abrir el modal.
const buildQuickConvertPayload = (oport) => {
  const adj = oport?.adjudicatario_data || {};
  const contacto = (adj.persona_contacto || "").trim();
  const email = (adj.email || "").trim();
  if (!contacto || !email) return null;
  return {
    contacto,
    email,
    telefono: adj.telefono || "",
    cargo: adj.cargo_contacto || adj.departamento || "",
    servicios: SERVICIOS_POR_TIPO[oport.tipo_srs] || [],
    valor_estimado: Math.round((oport.importe || 0) * 0.2),
    notas: `Lead creado desde SpotterSRS (quick-convert)\nContrato: ${oport.titulo || ""}\nImporte contrato: €${(oport.importe || 0).toLocaleString("es-ES")}`,
    feedback_conversion: [],
  };
};

// Get consistent color for user based on their ID
const getUserColor = (userId, users) => {
  if (!userId) return null;
  const index = users.findIndex(u => u.user_id === userId);
  return userColors[index % userColors.length];
};

// Fecha "heat" indicator — how recent is the adjudicación
const getFechaHeat = (dateStr) => {
  if (!dateStr) return { level: "none", color: "text-slate-600", bg: "bg-slate-700", dot: "bg-slate-600", label: "Sin fecha" };
  const now = new Date();
  const date = new Date(dateStr);
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays <= 7) return { level: "hot", color: "text-red-400", bg: "bg-red-500/20", dot: "bg-red-500", label: "< 1 semana" };
  if (diffDays <= 30) return { level: "warm", color: "text-orange-400", bg: "bg-orange-500/20", dot: "bg-orange-500", label: "< 1 mes" };
  if (diffDays <= 90) return { level: "mild", color: "text-yellow-400", bg: "bg-yellow-500/20", dot: "bg-yellow-500", label: "< 3 meses" };
  if (diffDays <= 180) return { level: "cool", color: "text-blue-400", bg: "bg-blue-500/20", dot: "bg-blue-400", label: "< 6 meses" };
  return { level: "cold", color: "text-slate-400", bg: "bg-slate-500/20", dot: "bg-slate-500", label: "> 6 meses" };
};

export default function Oportunidades({ user }) {
  const [oportunidades, setOportunidades] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tiposSrs, setTiposSrs] = useState([]);
  const [users, setUsers] = useState([]);
  // Filters
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todas");
  const [tipoFilter, setTipoFilter] = useState("todos");
  const [regionFilter, setRegionFilter] = useState("todos");
  const [scoreMin, setScoreMin] = useState("");
  // Signal filter: IA relevante OR score alto (reduce ruido ~87%)
  // Por defecto ACTIVO — el equipo solo ve oportunidades con señal real
  const [onlyRelevant, setOnlyRelevant] = useState(true);
  // Fondos UE: dolor temporal agudo (tiempos ajustados)
  const [conFondosUe, setConFondosUe] = useState(false);
  // v4.4.33 — filtro rango fechas (sobre fecha_adjudicacion)
  const [fechaRango, setFechaRango] = useState({ desde: null, hasta: null });

  // Sorting
  const [sortBy, setSortBy] = useState("score");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 50;

  // Modal
  const [selectedOportunidad, setSelectedOportunidad] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Descartar modal (feedback loop obligatorio)
  const [descartarModalOpen, setDescartarModalOpen] = useState(false);
  const [oportunidadADescartar, setOportunidadADescartar] = useState(null);
  // Convertir a lead (1-click desde tabla)
  const [convertirModalOpen, setConvertirModalOpen] = useState(false);
  const [oportunidadAConvertir, setOportunidadAConvertir] = useState(null);

  // Deep-link highlight
  const [highlightId, setHighlightId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchOportunidades = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (estadoFilter && estadoFilter !== "todas") params.append("estado", estadoFilter);
      if (tipoFilter && tipoFilter !== "todos") params.append("tipo_srs", tipoFilter);
      if (scoreMin) params.append("score_min", scoreMin);
      if (regionFilter && regionFilter !== "todos") params.append("lugar_ejecucion", regionFilter);
      if (search) params.append("search", search);
      if (onlyRelevant) params.append("only_relevant", "true");
      if (conFondosUe) params.append("con_fondos_ue", "true");
      // v4.4.33 — rango de fechas
      if (fechaRango.desde) params.append("fecha_desde", fechaRango.desde);
      if (fechaRango.hasta) params.append("fecha_hasta", fechaRango.hasta);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      params.append("skip", String((page - 1) * PAGE_SIZE));
      params.append("limit", String(PAGE_SIZE));

      const response = await api.get(`/oportunidades?${params.toString()}`);
      const { data, total, pages } = response.data;
      setOportunidades(data);
      setTotalCount(total);
      setTotalPages(pages);
    } catch (error) {
      toast.error("Error al cargar oportunidades");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [estadoFilter, tipoFilter, regionFilter, scoreMin, search, onlyRelevant, conFondosUe, fechaRango, page, sortBy, sortOrder]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/oportunidades/stats');
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Error cargando estadísticas");
    }
  };

  const fetchTiposSrs = async () => {
    try {
      const response = await api.get('/oportunidades/tipos-srs');
      setTiposSrs(response.data);
    } catch (error) {
      console.error("Error fetching tipos:", error);
      toast.error("Error cargando tipos SRS");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Error cargando usuarios");
    }
  };

  useEffect(() => {
    fetchTiposSrs();
    fetchStats();
    fetchUsers();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [estadoFilter, tipoFilter, scoreMin, search, onlyRelevant, conFondosUe]);

  useEffect(() => {
    fetchOportunidades();
  }, [fetchOportunidades]);

  // Deep-link: highlight row from ?open=ID
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && oportunidades.length > 0) {
      const match = oportunidades.find(
        (o) => o.oportunidad_id === openId || o._id === openId
      );
      if (match) {
        const rowId = match.oportunidad_id || match._id;
        setHighlightId(rowId);
        setSearchParams({}, { replace: true });
        setTimeout(() => {
          const el = document.getElementById(`opp-row-${rowId}`);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        setTimeout(() => setHighlightId(null), 5000);
      } else {
        const fetchAndOpen = async () => {
          try {
            const response = await api.get(`/oportunidades/${openId}`);
            setSelectedOportunidad(response.data);
            setModalOpen(true);
          } catch (error) {
            toast.error("No se pudo encontrar la oportunidad");
          } finally {
            setSearchParams({}, { replace: true });
          }
        };
        fetchAndOpen();
      }
    }
  }, [searchParams, oportunidades]); // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = (oportunidad) => {
    setSelectedOportunidad(oportunidad);
    setModalOpen(true);
    const id = oportunidad._id || oportunidad.id;
    if (id) setSearchParams({ open: id }, { replace: true });
  };

  const closeModal = () => {
    setSelectedOportunidad(null);
    setModalOpen(false);
    setSearchParams({}, { replace: true });
    fetchOportunidades();
    fetchStats();
  };

  // Cycle through estados: nueva -> revisada -> descartada -> nueva
  // Si el siguiente estado es "descartada", abre el modal de feedback (obligatorio)
  const cycleEstado = async (e, oport) => {
    e.stopPropagation(); // Don't open modal

    const estadoCycle = ["nueva", "revisada", "descartada"];
    const currentIndex = estadoCycle.indexOf(oport.estado);
    const nextIndex = (currentIndex + 1) % estadoCycle.length;
    const nextEstado = estadoCycle[nextIndex];

    // Si vamos a descartar, forzar feedback estructurado via DescartarModal
    if (nextEstado === "descartada") {
      setOportunidadADescartar(oport);
      setDescartarModalOpen(true);
      return;
    }

    try {
      await api.put(
        `/oportunidades/${oport.oportunidad_id}`,
        { estado: nextEstado }
      );
      // Update local state
      setOportunidades(prev =>
        prev.map(o => o.oportunidad_id === oport.oportunidad_id
          ? { ...o, estado: nextEstado }
          : o
        )
      );
      toast.success(`Estado: ${estadoConfig[nextEstado].label}`);
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  // Quick convert 1-click desde tabla:
  //   - Si la IA ya enriqueció la oportunidad (contacto + email del adjudicatario),
  //     hace POST directo sin abrir modal.
  //   - Si falta info, abre el modal como fallback.
  const openConvertir = async (e, oport) => {
    e.stopPropagation(); // no abrir modal de detalle

    const payload = buildQuickConvertPayload(oport);
    if (!payload) {
      // Fallback: abrir modal para que el usuario complete contacto/email
      setOportunidadAConvertir(oport);
      setConvertirModalOpen(true);
      return;
    }

    try {
      await api.post(`/oportunidades/${oport.oportunidad_id}/convertir`, payload);
      setOportunidades((prev) =>
        prev.map((o) =>
          o.oportunidad_id === oport.oportunidad_id ? { ...o, estado: "convertida" } : o
        )
      );
      fetchStats();
      toast.success(`Lead creado: ${payload.contacto} (${oport.adjudicatario})`);
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al crear lead";
      // Si el backend ya tiene el lead vinculado o falta data, abrir modal como fallback
      if (error.response?.status === 400 || error.response?.status === 422) {
        setOportunidadAConvertir(oport);
        setConvertirModalOpen(true);
      } else {
        toast.error(msg);
      }
    }
  };

  const handleConvertirSuccess = () => {
    if (oportunidadAConvertir) {
      // Marcar como convertida en el listado local
      setOportunidades(prev =>
        prev.map(o => o.oportunidad_id === oportunidadAConvertir.oportunidad_id
          ? { ...o, estado: "convertida" }
          : o
        )
      );
    }
    setConvertirModalOpen(false);
    setOportunidadAConvertir(null);
    fetchStats();
    toast.success("Lead creado — encuéntralo en Leads");
  };

  // Callback tras completar descarte con feedback
  const handleDescarteSuccess = () => {
    if (oportunidadADescartar) {
      setOportunidades(prev =>
        prev.map(o => o.oportunidad_id === oportunidadADescartar.oportunidad_id
          ? { ...o, estado: "descartada" }
          : o
        )
      );
    }
    setDescartarModalOpen(false);
    setOportunidadADescartar(null);
    fetchStats();
  };

  // Get user name by ID
  const getUserName = (userId) => {
    if (!userId) return null;
    const u = users.find(u => u.user_id === userId);
    return u ? u.name.split(" ")[0] : null; // First name only
  };

  // Get initials from name (Juan Gutierrez -> JG)
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  // Assign user to oportunidad
  const assignUser = async (e, oport, userId) => {
    e.stopPropagation(); // Don't open modal

    try {
      await api.put(
        `/oportunidades/${oport.oportunidad_id}`,
        { asignado_id: userId || null }
      );
      // Update local state
      setOportunidades(prev =>
        prev.map(o => o.oportunidad_id === oport.oportunidad_id
          ? { ...o, asignado_id: userId }
          : o
        )
      );
      const userName = userId ? users.find(u => u.user_id === userId)?.name : null;
      toast.success(userName ? `Asignado a ${userName}` : "Asignación eliminada");
    } catch (error) {
      toast.error("Error al asignar");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Toggle sort on a column
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // Sort icon helper
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortOrder === "desc"
      ? <ArrowDown className="w-3 h-3 ml-1 text-cyan-400" />
      : <ArrowUp className="w-3 h-3 ml-1 text-cyan-400" />;
  };

  const clearFilters = () => {
    setSearch("");
    setEstadoFilter("todas");
    setTipoFilter("todos");
    setRegionFilter("todos");
    setScoreMin("");
    setFechaRango({ desde: null, hasta: null });
    setSortBy("score");
    setSortOrder("desc");
    setPage(1);
  };

  const hasFilters = search || estadoFilter !== "todas" || tipoFilter !== "todos" || regionFilter !== "todos" || scoreMin || fechaRango.desde || fechaRango.hasta || sortBy !== "score" || sortOrder !== "desc";

  return (
    <div className="space-y-6" data-testid="oportunidades-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-display theme-text flex items-center gap-2">
            <Radar className="w-7 h-7 text-teal-400" />
            SpotterSRS - Oportunidades
          </h1>
          <p className="theme-text-secondary text-sm">
            Adjudicaciones PLACSP detectadas para subcontratación
          </p>
        </div>

        {/* Signal filter toggles — patrón switch ON/OFF con texto fijo */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setOnlyRelevant(!onlyRelevant)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-normal ease-spring border ${
              onlyRelevant
                ? "bg-teal-400/15 text-teal-400 border-teal-400/40 shadow-glow-primary"
                : "theme-bg-tertiary theme-text-secondary border-transparent hover:border-teal-400/30"
            }`}
            title={onlyRelevant
              ? "ACTIVO: mostrando solo oportunidades con señal real. Click para ver TODAS incluido ruido."
              : "INACTIVO: mostrando todas. Click para filtrar solo las relevantes."}
            aria-pressed={onlyRelevant}
          >
            <Zap className="w-4 h-4" />
            <span>Con Señal</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              onlyRelevant ? "bg-teal-400/30 text-teal-300" : "bg-slate-700 text-slate-400"
            }`}>
              {onlyRelevant ? "ON" : "OFF"}
            </span>
          </button>
          <button
            onClick={() => setConFondosUe(!conFondosUe)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-normal ease-spring border ${
              conFondosUe
                ? "bg-blue-500/15 text-blue-400 border-blue-400/40"
                : "theme-bg-tertiary theme-text-secondary border-transparent hover:border-blue-400/30"
            }`}
            title={conFondosUe
              ? "ACTIVO: solo oportunidades con fondos europeos (dolor temporal). Click para ver todas."
              : "INACTIVO: click para filtrar solo las que tienen fondos europeos."}
            aria-pressed={conFondosUe}
          >
            <span className="text-sm">🇪🇺</span>
            <span>Fondos UE</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
              conFondosUe ? "bg-blue-400/30 text-blue-300" : "bg-slate-700 text-slate-400"
            }`}>
              {conFondosUe ? "ON" : "OFF"}
            </span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Total</p>
                  <p className="text-2xl font-bold theme-text">{stats.total}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Nuevas</p>
                  <p className="text-2xl font-bold text-cyan-400">{stats.nuevas}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-cyan-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Valor Total</p>
                  <p className="text-2xl font-bold text-green-400">{formatCurrency(stats.valor_total)}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="theme-bg-secondary border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs theme-text-muted uppercase tracking-wide">Convertidas</p>
                  <p className="text-2xl font-bold text-green-400">{stats.por_estado?.convertida || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="theme-bg-secondary border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-6 items-center">
            {/* Left: Count Box */}
            <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-4 min-w-[140px] flex flex-col justify-center items-center text-center shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-1">OPORTUNIDADES</p>
              <p className="text-3xl font-bold text-yellow-400 leading-none">{totalCount}</p>
            </div>

            {/* Right: Filters Grid — v4.4.33 col extra para DateRange */}
            <div className="flex-1 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto_auto] gap-3 items-center">
              {/* Search */}
              <div className="relative w-full">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                <Input
                  placeholder="Buscar por expediente, título..."
                  className="pl-9 bg-slate-950 border-slate-800 focus:border-cyan-500/50 transition-colors h-10"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Estado */}
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="todas">Todos los estados</SelectItem>
                  <SelectItem value="nueva">Nueva</SelectItem>
                  <SelectItem value="revisada">Revisada</SelectItem>
                  <SelectItem value="contactada">Contactada</SelectItem>
                  <SelectItem value="convertida">Convertida</SelectItem>
                  <SelectItem value="descartada">Descartada</SelectItem>
                </SelectContent>
              </Select>

              {/* Tipo */}
              <Select value={tipoFilter} onValueChange={setTipoFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Tipo SRS" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="todos">Todos los tipos</SelectItem>
                  <SelectItem value="sin_tipo">Sin tipo</SelectItem>
                  {tiposSrs.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Región */}
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <MapPin className="w-3.5 h-3.5 mr-1 text-slate-500" />
                  <SelectValue placeholder="Región" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                  <SelectItem value="todos">Todas regiones</SelectItem>
                  {stats?.por_region &&
                    Object.entries(stats.por_region)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([region, count]) => (
                        <SelectItem key={region} value={region}>
                          {region} ({count})
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>

              {/* Score Min */}
              <Select value={scoreMin || "none"} onValueChange={(v) => setScoreMin(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-slate-950 border-slate-800 h-10">
                  <SelectValue placeholder="Min Score" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="none">Sin mínimo</SelectItem>
                  <SelectItem value="85">≥ 85 (Crítico)</SelectItem>
                  <SelectItem value="70">≥ 70 (Alto)</SelectItem>
                  <SelectItem value="50">≥ 50 (Medio)</SelectItem>
                </SelectContent>
              </Select>

              {/* v4.4.33 — Filtro fecha adjudicación */}
              <div className="flex justify-center">
                <DateRangeFilter
                  value={fechaRango}
                  onChange={setFechaRango}
                  label="Adjudicación"
                  compact
                />
              </div>

              {/* Clear Button - Fixed visibility */}
              <div className="flex justify-center">
                {hasFilters ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={clearFilters}
                    className="h-10 w-10 text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors rounded-full"
                    title="Limpiar filtros"
                  >
                    <XCircle className="w-5 h-5" />
                  </Button>
                ) : (
                  <div className="w-10 h-10" /> /* Spacer to keep grid alignment */
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card >

      {/* Table */}
      < Card className="theme-bg-secondary border-white/10 overflow-hidden" >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead
                  className="text-slate-400 w-[80px] cursor-pointer hover:text-cyan-400 transition-colors select-none"
                  onClick={() => toggleSort("score")}
                >
                  <span className="flex items-center">SRS<SortIcon field="score" /></span>
                </TableHead>
                <TableHead
                  className="text-slate-400 w-[90px] cursor-pointer hover:text-teal-400 transition-colors select-none"
                  onClick={() => toggleSort("score_subcontratacion")}
                  title="Probabilidad de que el ganador nos necesite (subcontratación)"
                >
                  <span className="flex items-center">Subcontrata<SortIcon field="score_subcontratacion" /></span>
                </TableHead>
                <TableHead className="text-slate-400 w-[140px] hidden md:table-cell">Expediente</TableHead>
                <TableHead className="text-slate-400">Adjudicatario</TableHead>
                <TableHead
                  className="text-slate-400 text-right cursor-pointer hover:text-cyan-400 transition-colors select-none"
                  onClick={() => toggleSort("importe")}
                >
                  <span className="flex items-center justify-end">Importe<SortIcon field="importe" /></span>
                </TableHead>
                <TableHead className="text-slate-400 hidden lg:table-cell">Tipo SRS</TableHead>
                <TableHead className="text-slate-400 hidden lg:table-cell">Región</TableHead>
                <TableHead className="text-slate-400">Estado</TableHead>
                <TableHead className="text-slate-400 hidden md:table-cell">Asignado</TableHead>
                <TableHead
                  className="text-slate-400 cursor-pointer hover:text-cyan-400 transition-colors select-none"
                  onClick={() => toggleSort("fecha_adjudicacion")}
                >
                  <span className="flex items-center">Fecha<SortIcon field="fecha_adjudicacion" /></span>
                </TableHead>
                <TableHead className="text-slate-400 w-[50px] hidden md:table-cell"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-cyan-400" />
                    <p className="mt-2 theme-text-muted">Cargando oportunidades...</p>
                  </TableCell>
                </TableRow>
              ) : oportunidades.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-12">
                    <Target className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                    <p className="theme-text-muted">No se encontraron oportunidades</p>
                    {hasFilters && (
                      <Button
                        variant="link"
                        onClick={clearFilters}
                        className="text-cyan-400 mt-2"
                      >
                        Limpiar filtros
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                oportunidades.map((oport) => {
                  const scoreLevel = getScoreLevel(oport.score);
                  const estado = estadoConfig[oport.estado] || estadoConfig.nueva;
                  const EstadoIcon = estado.icon;

                  return (
                    <TableRow
                      key={oport.oportunidad_id}
                      id={`opp-row-${oport.oportunidad_id}`}
                      className={`border-slate-800 hover:bg-slate-800/50 cursor-pointer transition-all duration-500 ${
                        highlightId === oport.oportunidad_id
                          ? "bg-cyan-500/15 ring-1 ring-cyan-500/50 animate-pulse"
                          : ""
                      }`}
                      onClick={() => openModal(oport)}
                      data-testid={`oportunidad-row-${oport.oportunidad_id}`}
                    >
                      {/* Score SRS (licitación) */}
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-8 rounded-full ${scoreLevel.color}`} />
                          <span className={`font-bold font-mono ${scoreLevel.textColor}`}>
                            {oport.score}
                          </span>
                        </div>
                      </TableCell>

                      {/* Score Subcontratación (ganar-ganar) */}
                      <TableCell>
                        {(() => {
                          const sc = oport.score_subcontratacion || 0;
                          const nivel = oport.nivel_subcontratacion || "EXCLUIDO";
                          const colors = {
                            CRITICO: "text-teal-400 bg-teal-400/15",
                            ALTO: "text-amber-400 bg-amber-400/15",
                            MEDIO: "text-blue-400 bg-blue-400/15",
                            BAJO: "text-slate-400 bg-slate-500/15",
                            EXCLUIDO: "text-slate-600 bg-slate-800/30",
                          };
                          const cls = colors[nivel] || colors.EXCLUIDO;
                          return (
                            <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md ${cls}`}>
                              <span className="font-bold font-mono text-sm">{sc}</span>
                            </div>
                          );
                        })()}
                      </TableCell>

                      {/* Expediente */}
                      <TableCell className="font-mono text-xs text-slate-400 hidden md:table-cell">
                        {oport.expediente_id || oport.ref_placsp || "-"}
                      </TableCell>

                      {/* Adjudicatario */}
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium theme-text truncate max-w-[120px] sm:max-w-[250px]">
                              {oport.adjudicatario}
                            </p>
                            {(oport.tiene_fondos_ue || oport.fondos_ue) && (
                              <Badge
                                className="bg-blue-500/20 text-blue-400 border-blue-400/40 text-[10px] px-1.5 py-0 font-semibold"
                                title={oport.fondos_ue || "Fondos europeos"}
                              >
                                🇪🇺 UE
                              </Badge>
                            )}
                            {oport.es_partner_srs && (
                              <Badge className="bg-teal-500/20 text-teal-400 border-teal-400/40 text-[10px] px-1.5 py-0 font-semibold">
                                PARTNER
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs theme-text-muted truncate max-w-[120px] sm:max-w-[250px]">
                            {oport.titulo}
                          </p>
                        </div>
                      </TableCell>

                      {/* Importe */}
                      <TableCell className="text-right font-semibold font-mono text-green-400">
                        {formatCurrency(oport.importe)}
                      </TableCell>

                      {/* Tipo SRS */}
                      <TableCell className="hidden lg:table-cell">
                        {oport.tipo_srs ? (
                          <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                            {oport.tipo_srs.split(" / ")[0]}
                          </Badge>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </TableCell>

                      {/* Región */}
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-slate-500" />
                          {oport.lugar_ejecucion || oport.localizacion || "-"}
                        </span>
                      </TableCell>

                      {/* Estado - Clickeable */}
                      <TableCell>
                        <Badge
                          className={`${estado.bg} ${estado.color} border-0 cursor-pointer hover:opacity-80 transition-opacity`}
                          onClick={(e) => cycleEstado(e, oport)}
                          title="Click para cambiar estado"
                        >
                          <EstadoIcon className="w-3 h-3 mr-1" />
                          {estado.label}
                        </Badge>
                      </TableCell>

                      {/* Asignado - Círculo con iniciales */}
                      <TableCell className="hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={oport.asignado_id || "none"}
                          onValueChange={(v) => assignUser({ stopPropagation: () => { } }, oport, v === "none" ? null : v)}
                        >
                          <SelectTrigger className="w-10 h-10 rounded-full p-0 border-0 bg-transparent hover:bg-slate-700/30 transition-colors flex items-center justify-center">
                            {oport.asignado_id ? (
                              <div className={`w-9 h-9 rounded-full border-2 ${getUserColor(oport.asignado_id, users)?.border || 'border-slate-500'} bg-slate-900/50 flex items-center justify-center ${getUserColor(oport.asignado_id, users)?.text || 'text-slate-400'} text-sm font-bold`}>
                                {getInitials(users.find(u => u.user_id === oport.asignado_id)?.name)}
                              </div>
                            ) : (
                              <div className="w-9 h-9 rounded-full border-2 border-slate-600 bg-slate-900/50 flex items-center justify-center text-slate-500">
                                <User className="w-4 h-4" />
                              </div>
                            )}
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-slate-700">
                            <SelectItem value="none" className="text-slate-400">
                              <div className="flex items-center gap-2">
                                <XCircle className="w-4 h-4 text-red-400" />
                                <span>Sin asignar</span>
                              </div>
                            </SelectItem>
                            {users.map((u, idx) => {
                              const color = userColors[idx % userColors.length];
                              return (
                                <SelectItem key={u.user_id} value={u.user_id}>
                                  <div className="flex items-center gap-2">
                                    <div className={`w-7 h-7 rounded-full border-2 ${color.border} bg-slate-900/50 flex items-center justify-center ${color.text} text-xs font-bold`}>
                                      {getInitials(u.name)}
                                    </div>
                                    <span className="text-slate-300">{u.name}</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </TableCell>

                      {/* Fecha con indicador de calor
                           v4.4.6: priorizamos fecha_primera_adjudicacion (cuándo la detectamos
                           como ADJ por primera vez) sobre fecha_adjudicacion (la que pone PLACSP),
                           porque es más precisa para saber "esto es fresco" */}
                      <TableCell className="text-sm hidden md:table-cell">
                        {(() => {
                          const fechaRef = oport.fecha_primera_adjudicacion || oport.fecha_adjudicacion;
                          const heat = getFechaHeat(fechaRef);
                          return (
                            <div className="flex items-center gap-2" title={heat.label}>
                              <div className={`w-2 h-2 rounded-full ${heat.dot} flex-shrink-0`} />
                              <span className={heat.color}>
                                {formatDate(oport.fecha_adjudicacion)}
                              </span>
                            </div>
                          );
                        })()}
                      </TableCell>

                      {/* Acciones: Quick-convert + Link PLACSP */}
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-1">
                          {oport.estado !== "convertida" && (() => {
                            const quickReady = !!buildQuickConvertPayload(oport);
                            return (
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 relative ${quickReady ? "text-teal-400 hover:text-teal-300 hover:bg-teal-500/10" : "text-slate-500 hover:text-slate-300"}`}
                                title={quickReady ? "Convertir a Lead (1-click — datos IA listos)" : "Convertir a Lead (abrirá modal para completar contacto)"}
                                onClick={(e) => openConvertir(e, oport)}
                              >
                                <UserPlus className="w-4 h-4" />
                                {quickReady && (
                                  <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-teal-400 shadow-[0_0_4px_rgba(45,212,191,0.8)]" />
                                )}
                              </Button>
                            );
                          })()}
                          {oport.url_placsp && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-cyan-400"
                              title="Abrir en PLACSP"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(oport.url_placsp, "_blank");
                              }}
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {
          totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, totalCount)} de{" "}
                <span className="font-semibold text-slate-200">{totalCount.toLocaleString("es-ES")}</span>
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 text-sm text-slate-300">
                  Pág. <span className="font-semibold text-white">{page}</span> / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-400 hover:text-white disabled:opacity-30"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )
        }
      </Card >

      {/* Modal */}
      <OportunidadModal
        open={modalOpen}
        onClose={closeModal}
        oportunidad={selectedOportunidad}
        user={user}
      />

      {/* Descartar modal con feedback obligatorio */}
      <DescartarModal
        open={descartarModalOpen}
        onClose={() => {
          setDescartarModalOpen(false);
          setOportunidadADescartar(null);
        }}
        oportunidad={oportunidadADescartar}
        onSuccess={handleDescarteSuccess}
      />

      {/* Quick-convert modal (1-click desde tabla) */}
      <ConvertirLeadModal
        open={convertirModalOpen}
        onClose={() => {
          setConvertirModalOpen(false);
          setOportunidadAConvertir(null);
        }}
        oportunidad={oportunidadAConvertir}
        onSuccess={handleConvertirSuccess}
      />
    </div>
  );
}
